import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, Target, Maximize2, Plus } from "lucide-react";
import imageCompression from "browser-image-compression";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function ResizeImage50KB({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("jpeg");
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const TARGET_SIZE_KB = 50;
  const TARGET_SIZE_BYTES = TARGET_SIZE_KB * 1024;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const updateSEOTags = (seo, fallbackTitle) => {
    document.title = seo?.meta_title || fallbackTitle || `Resize Image to ${TARGET_SIZE_KB}KB - KayTool`;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seo?.meta_description || `Resize images to under ${TARGET_SIZE_KB}KB with our free online tool.`);
    
    if (seo?.meta_keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', seo.meta_keywords);
    }
  };

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/resize-image-to-50kb`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          updateSEOTags(data.seo, data.title);
        }
      } catch (error) {
        console.log('No CMS content for resize-image-to-50kb page');
        updateSEOTags(null, `Resize Image to ${TARGET_SIZE_KB}KB - KayTool`);
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }
  }, [embedded]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith("image/"));
    if (files.length > 0) {
      processMultipleImages(files);
    } else {
      toast.error("Please drop valid image files");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processMultipleImages(files);
    }
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) {
          const extension = file.type.split('/')[1] || 'png';
          const namedFile = new File([file], `pasted-image-${Date.now()}.${extension}`, { type: file.type });
          imageFiles.push(namedFile);
        }
      }
    }
    
    if (imageFiles.length > 0) {
      e.preventDefault();
      processMultipleImages(imageFiles);
      toast.success(`${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} pasted!`);
    }
  };

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const convertHeicToJpeg = async (file) => {
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    return new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const compressToTargetSize = async (file, targetSizeBytes, format) => {
    const dimensions = await getImageDimensions(file);
    let currentFile = file;
    let quality = 0.9;
    let scale = 1.0;
    const minQuality = 0.1;
    const minScale = 0.1;
    let iterations = 0;
    const maxIterations = 15;
    
    while (currentFile.size > targetSizeBytes && iterations < maxIterations) {
      iterations++;
      
      const options = {
        maxSizeMB: targetSizeBytes / (1024 * 1024),
        maxWidthOrHeight: Math.max(100, Math.round(Math.max(dimensions.width, dimensions.height) * scale)),
        useWebWorker: true,
        fileType: `image/${format}`,
        initialQuality: quality,
      };
      
      try {
        currentFile = await imageCompression(file, options);
        
        if (currentFile.size <= targetSizeBytes) break;
        
        if (quality > minQuality) {
          quality = Math.max(minQuality, quality - 0.15);
        } else if (scale > minScale) {
          scale = Math.max(minScale, scale - 0.15);
          quality = 0.7;
        } else {
          break;
        }
      } catch (error) {
        console.error('Compression error:', error);
        break;
      }
    }
    
    return currentFile;
  };

  const processMultipleImages = async (files) => {
    setIsProcessing(true);
    setCompressionProgress(0);

    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      setCompressionProgress(Math.round(((i) / files.length) * 100));
      
      try {
        if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
          file = await convertHeicToJpeg(file);
        }

        const dimensions = await getImageDimensions(file);
        const compressed = await compressToTargetSize(file, TARGET_SIZE_BYTES, outputFormat);
        const compressedDimensions = await getImageDimensions(compressed);
        
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalFile: file,
          originalSize: file.size,
          originalDimensions: dimensions,
          compressedFile: compressed,
          compressedSize: compressed.size,
          compressedDimensions: compressedDimensions,
          preview: URL.createObjectURL(compressed),
          originalPreview: URL.createObjectURL(file),
          status: compressed.size <= TARGET_SIZE_BYTES ? 'success' : 'warning',
        });
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
        toast.error(`Failed to process ${file.name}`);
      }
    }

    setImages(prev => [...prev, ...newImages]);
    setCompressionProgress(100);
    setIsProcessing(false);
    
    if (newImages.length > 0) {
      toast.success(`${newImages.length} image${newImages.length > 1 ? 's' : ''} resized!`);
    }
  };

  const downloadImage = (image) => {
    const link = document.createElement("a");
    link.href = image.preview;
    const extension = outputFormat === 'jpeg' ? 'jpg' : outputFormat;
    link.download = image.name.replace(/\.[^/.]+$/, `-resized-${TARGET_SIZE_KB}kb.${extension}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllAsZip = async () => {
    if (images.length === 0) return;
    
    const zip = new JSZip();
    const extension = outputFormat === 'jpeg' ? 'jpg' : outputFormat;
    
    for (const image of images) {
      const response = await fetch(image.preview);
      const blob = await response.blob();
      const fileName = image.name.replace(/\.[^/.]+$/, `-resized-${TARGET_SIZE_KB}kb.${extension}`);
      zip.file(fileName, blob);
    }
    
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `resized-images-${TARGET_SIZE_KB}kb.zip`);
    toast.success("Downloaded all images as ZIP!");
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const clearAll = () => {
    setImages([]);
    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success("All images cleared");
  };

  const handleAddMore = (e) => {
    const files = e.target.files;
    if (files) {
      handleFileSelect({ target: { files } });
    }
    // Reset input to allow selecting the same files again
    e.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (embedded) {
    return (
      <div className="p-4">
        <p className="text-center text-slate-500">Resize Image to {TARGET_SIZE_KB}KB Tool</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-blue-50'}`}>
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Maximize2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 dark:text-white">Resize to {TARGET_SIZE_KB}KB</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">by KayTool</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
              <Zap className="w-3 h-3" /> Auto-resize to {TARGET_SIZE_KB}KB
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="text-slate-600 dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-3">
            Resize Image to {TARGET_SIZE_KB}KB
          </h2>
          <p className="text-slate-600 dark:text-slate-300 max-w-xl mx-auto">
            Drop any image and get it resized to under {TARGET_SIZE_KB}KB automatically. Perfect for email attachments and web uploads.
          </p>
        </div>

        {/* Format Selection */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-lg p-2 shadow-sm border border-slate-200 dark:border-slate-700">
            <span className="text-sm text-slate-600 dark:text-slate-300 pl-2">Output:</span>
            <Select value={outputFormat} onValueChange={setOutputFormat}>
              <SelectTrigger className="w-28 border-0 bg-slate-100 dark:bg-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="jpeg">JPEG</SelectItem>
                <SelectItem value="png">PNG</SelectItem>
                <SelectItem value="webp">WebP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Upload Zone */}
        <div
          data-testid="upload-zone"
          className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer
            ${isDragging 
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20 scale-[1.02]' 
              : 'border-slate-300 dark:border-slate-600 hover:border-green-400 dark:hover:border-green-500 bg-white dark:bg-slate-800'
            }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <div className="flex flex-col items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
              isDragging ? 'bg-green-100 dark:bg-green-900/40' : 'bg-slate-100 dark:bg-slate-700'
            }`}>
              <Upload className={`w-8 h-8 ${isDragging ? 'text-green-500' : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">
                Drop, paste, or click to upload
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Ctrl+V to paste from clipboard • Multiple files supported
              </p>
            </div>
            <Badge className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-100">
              Auto-resize to {TARGET_SIZE_KB}KB
            </Badge>
          </div>
        </div>

        {/* Processing Progress */}
        {isProcessing && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600 dark:text-slate-300">Resizing images...</span>
              <span className="text-sm font-medium text-green-600">{compressionProgress}%</span>
            </div>
            <Progress value={compressionProgress} className="h-2" />
          </div>
        )}

        {/* Results */}
        {images.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Resized Images ({images.length})
              </h3>
              <div className="flex gap-2 flex-wrap">
                {/* Add More Button */}
                <input
                  ref={addMoreInputRef}
                  type="file"
                  accept="image/*,.heic"
                  multiple
                  onChange={handleAddMore}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => addMoreInputRef.current?.click()}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                {images.length > 1 && (
                  <Button size="sm" onClick={downloadAllAsZip} className="bg-green-600 hover:bg-green-700">
                    <Download className="w-4 h-4 mr-1" /> Download All (ZIP)
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                  <div className="aspect-video relative bg-slate-100 dark:bg-slate-700">
                    <img src={image.preview} alt={image.name} className="w-full h-full object-contain" />
                    <button
                      onClick={() => removeImage(image.id)}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <Badge className={`absolute bottom-2 left-2 ${
                      image.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'
                    }`}>
                      {formatFileSize(image.compressedSize)}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate text-sm">{image.name}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>{formatFileSize(image.originalSize)}</span>
                      <span>→</span>
                      <span className="text-green-600 dark:text-green-400 font-medium">{formatFileSize(image.compressedSize)}</span>
                      <span className="ml-auto text-green-600 dark:text-green-400">
                        -{Math.round((1 - image.compressedSize / image.originalSize) * 100)}%
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      {image.originalDimensions.width}×{image.originalDimensions.height} → {image.compressedDimensions.width}×{image.compressedDimensions.height}
                    </div>
                    <Button onClick={() => downloadImage(image)} className="w-full mt-3 bg-green-600 hover:bg-green-700" size="sm">
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <Zap className="w-8 h-8 text-green-500 mb-3" />
            <h3 className="font-semibold text-slate-800 dark:text-white">Instant</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">No settings needed</p>
          </Card>
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <Target className="w-8 h-8 text-green-500 mb-3" />
            <h3 className="font-semibold text-slate-800 dark:text-white">{TARGET_SIZE_KB}KB Target</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Perfect for emails</p>
          </Card>
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <Info className="w-8 h-8 text-green-500 mb-3" />
            <h3 className="font-semibold text-slate-800 dark:text-white">100% Private</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Browser-only processing</p>
          </Card>
        </div>

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {pageContent?.content && pageContent.content.length > 0 && (
        <div className="bg-slate-50 dark:bg-slate-800/50 py-12">
          <div className="max-w-6xl mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}


      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          <p>© 2025 KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default ResizeImage50KB;
